import type { ReactElement, ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { Dashboard } from "./layout/Dashboard";
import { Loader } from "./components/Loader";
import { LoginPage } from "./pages/LoginPage";
import { OverviewPage } from "./pages/OverviewPage";
import { GetStartedPage } from "./pages/GetStartedPage";
import { PeoplePage } from "./pages/PeoplePage";
import { MembershipPage } from "./pages/MembershipPage";
import { InvitesPage } from "./pages/InvitesPage";
import { InviteAttemptsPage } from "./pages/InviteAttemptsPage";
import { CatalogsPage } from "./pages/CatalogsPage";
import { ManifestEditorPage } from "./pages/manifestEditor/ManifestEditorPage";
import { FeedbackPage } from "./pages/FeedbackPage";
import { ReportsPage } from "./pages/ReportsPage";
import { NoticesPage } from "./pages/NoticesPage";
import { ProgressPage } from "./pages/ProgressPage";
import { DevicesPage } from "./pages/DevicesPage";
import { ProviderSettingsPage } from "./pages/ProviderSettingsPage";
import { UsagePage } from "./pages/UsagePage";
import { AuditPage } from "./pages/AuditPage";
import { UtilitiesPage } from "./pages/UtilitiesPage";
import { AppLinksPage } from "./pages/AppLinksPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { AlertsPage } from "./pages/AlertsPage";
import { MonitoringPage } from "./pages/MonitoringPage";
import { BulkOperationsPage } from "./pages/BulkOperationsPage";
import { SecurityPage } from "./pages/SecurityPage";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { FilesPage } from "./pages/FilesPage";
import { ADMIN_NAV_ITEMS } from "./layout/navItems";
import { useAdminFeatures } from "./features/AdminFeaturesContext";

/** Route guard — mirrors `apps/admin-web/src/App.tsx`'s `RequireAdmin` (itself mirroring
 * `apps/admin/app/index.tsx`'s `Redirect`-once-signed-in gate), just with the vendored `Loader`
 * (a Mantis-style top progress bar) instead of a centered `CSpinner`/`CircularProgress`. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { ready, isAdmin } = useAuth();
  if (!ready) return <Loader />;
  if (!isAdmin) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * `ADMIN_NAV_ITEMS.id` → the built page element it routes to — kept as a lookup rather
 * than inline JSX per `<Route>` so the docs/358 feature filter (below) can decide which
 * routes to MOUNT at all, not just which to render inert. A disabled feature's route is
 * simply absent from the tree, so a deep link to it falls through to the existing
 * catch-all `<Route path="*">` redirect (docs/358 §4.3's "route gating point").
 */
const PAGE_ELEMENTS: Record<string, ReactElement> = {
  overview: <OverviewPage />,
  "get-started": <GetStartedPage />,
  people: <PeoplePage />,
  membership: <MembershipPage />,
  invites: <InvitesPage />,
  "invite-attempts": <InviteAttemptsPage />,
  catalogs: <CatalogsPage />,
  manifests: <ManifestEditorPage />,
  files: <FilesPage />,
  feedback: <FeedbackPage />,
  reports: <ReportsPage />,
  notices: <NoticesPage />,
  progress: <ProgressPage />,
  devices: <DevicesPage />,
  "provider-settings": <ProviderSettingsPage />,
  security: <SecurityPage />,
  usage: <UsagePage />,
  audit: <AuditPage />,
  utilities: <UtilitiesPage />,
  "app-links": <AppLinksPage />,
  analytics: <AnalyticsPage />,
  alerts: <AlertsPage />,
  monitoring: <MonitoringPage />,
  "bulk-operations": <BulkOperationsPage />,
};

/**
 * Routing shape copied from `apps/admin-web/src/App.tsx` (docs/337 §"REUSE verbatim" — `App.tsx`'s
 * `<Routes>` + `RequireAdmin` guard) — only the shell it wraps (`Dashboard` instead of
 * `DefaultLayout`) differs. All 12 original `ADMIN_NAV_ITEMS` destinations are real pages as of
 * docs/337 v2 (full parity with `apps/admin-web`), plus a 13th, mantis-only `/audit`
 * (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md Part B), a 14th, `/overview` (docs/340 v1 §4.1 —
 * the new landing route, `index` now points here instead of `/people`), a 15th,
 * mantis-only `/utilities` (docs/356-ADMIN-UTILITIES-PAGE.md v0's QR slice), a 16th,
 * mantis-only `/analytics` (docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0), and a 17th/18th,
 * mantis-only `/alerts`+`/monitoring` (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0); `ComingSoonPage`
 * stays wired for any future addition.
 *
 * **The route gating point** (docs/358-ADMIN-FEATURE-CONFIGURATION.md v0) — every route is
 * mounted only when `useAdminFeatures().has(item.feature)` (§4.3: "make the route list read
 * the descriptor," extending the existing `built`/`ComingSoon` filter pattern rather than
 * wrapping each `<Route>` in a separate guard component). The `index` redirect target is
 * computed from the FIRST enabled nav item (never hardcoded `/overview`) so a deployment that
 * disables `overview` can't redirect into a route that isn't mounted.
 */
export default function App() {
  const { has, hasCapability } = useAdminFeatures();
  const enabledItems = ADMIN_NAV_ITEMS.filter(
    (item) => has(item.feature) && (!item.requires || hasCapability(item.requires)),
  );
  const indexTarget = enabledItems[0]?.route ?? "/login";

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAdmin>
            <Dashboard />
          </RequireAdmin>
        }
      >
        <Route index element={<Navigate to={indexTarget} replace />} />
        {enabledItems
          .filter((item) => item.built)
          .map((item) => (
            <Route key={item.id} path={item.route} element={PAGE_ELEMENTS[item.id]} />
          ))}
        {enabledItems
          .filter((item) => !item.built)
          .map((item) => (
            <Route key={item.id} path={item.route} element={<ComingSoonPage title={item.label} />} />
          ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
